import { useEffect, useRef, useState } from "react";

const THRESHOLD = 80; // kaç px çekilince refresh tetiklensin
const MAX_PULL = 120;  // maksimum görsel çekiş mesafesi

function PullToRefresh() {
  const [pullY, setPullY] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startYRef = useRef<number | null>(null);
  const pullingRef = useRef(false);

  useEffect(() => {
    // Sadece touch cihazlarda aktif
    if (!("ontouchstart" in window)) return;

    const isModalOpen = () => document.body.classList.contains("modal-open");

    const onTouchStart = (e: TouchEvent) => {
      // Modal/popup açıksa pull-to-refresh devre dışı
      if (isModalOpen()) return;
      // Sadece sayfanın en üstündeyken başlat
      if (window.scrollY > 0) return;
      startYRef.current = e.touches[0].clientY;
      pullingRef.current = false;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (startYRef.current === null || refreshing) return;
      const delta = e.touches[0].clientY - startYRef.current;
      if (delta <= 0) {
        startYRef.current = null;
        return;
      }
      // Sayfa en üstteyse varsayılan scroll'u engelle
      if (window.scrollY === 0 && delta > 0) {
        pullingRef.current = true;
        e.preventDefault();
        setPullY(Math.min(delta, MAX_PULL));
      }
    };

    const onTouchEnd = () => {
      if (!pullingRef.current) return;
      if (pullY >= THRESHOLD) {
        setRefreshing(true);
        setTimeout(() => window.location.reload(), 500);
      } else {
        setPullY(0);
      }
      startYRef.current = null;
      pullingRef.current = false;
    };

    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchmove", onTouchMove, { passive: false });
    document.addEventListener("touchend", onTouchEnd);
    return () => {
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);
    };
  }, [pullY, refreshing]);

  if (pullY === 0 && !refreshing) return null;

  const progress = Math.min(pullY / THRESHOLD, 1);
  const size = 28;
  const radius = 11;
  const circumference = 2 * Math.PI * radius;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 2000,
        display: "flex",
        justifyContent: "center",
        transform: `translateY(${Math.min(pullY, MAX_PULL) - size}px)`,
        transition: pullY === 0 ? "transform 0.3s ease" : "none",
        pointerEvents: "none",
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: "50%",
          width: `${size + 8}px`,
          height: `${size + 8}px`,
          boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {refreshing ? (
          <svg
            width={size}
            height={size}
            viewBox={`0 0 ${size} ${size}`}
            style={{ animation: "ptr-spin 0.8s linear infinite" }}
          >
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke="#c20e1a"
              strokeWidth="2.5"
              strokeDasharray={`${circumference * 0.75} ${circumference * 0.25}`}
            />
          </svg>
        ) : (
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke="#e0e0e0"
              strokeWidth="2.5"
            />
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke="#c20e1a"
              strokeWidth="2.5"
              strokeDasharray={`${circumference * progress} ${circumference * (1 - progress)}`}
              strokeLinecap="round"
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
            />
          </svg>
        )}
      </div>
      <style>{`
        @keyframes ptr-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default PullToRefresh;
